// shared-lib/dimensioning/rules.js
//
// The dimension rules: given a user's stated intent, what gets drawn.
//
// Each rule is a PURE resolver `(record, ctx) -> Measurement[]` in plan
// coordinates. It emits geometry and an intended STYLE; it does not know about
// tiers, canvases or SVG. Both generators adapt the output to their own
// drawing layer, which is what lets one statement print in the plan and the
// elevation alike.
//
// See claude-docs/2026-09-15-unified-dimensioning.md §3.1.

const { normalizeRef } = require('../group-customization/refs');
const {
  resolveDatum,
  resolveAxis,
  dotXZ,
  datumExtensionAnchor,
  datumEdgeToward,
  axisModeFor,
} = require('./datums');

// Two legs count as equal — and print as EQ rather than twice the same number
// — within the measured 0.5" noise floor the link graph already uses for
// `center-on`. Not a new tolerance: the same one, for the same judgement.
const EQ_TOLERANCE_M = 0.0127;

const lookupEntity = (id, ctx) => {
  const byId = ctx?.entitiesById;
  if (!byId) return null;
  return typeof byId.get === 'function' ? byId.get(id) : byId[id];
};

/** The subject's centre as a point, or null when it cannot be placed. */
function subjectCentre(record, ctx) {
  const r = normalizeRef(record?.subject);
  if (!r) return null;
  const entity = lookupEntity(r.id, ctx);
  const c = entity?.center;
  if (!c || typeof c.x !== 'number' || typeof c.z !== 'number') return null;
  return [c.x, c.z];
}

/** The point at scalar `s` on the axis line through `origin`. */
const pointAt = (origin, u, s) => {
  const s0 = origin[0] * u[0] + origin[1] * u[1];
  return [origin[0] + (s - s0) * u[0], origin[1] + (s - s0) * u[1]];
};

function leg({
  rule,
  subjectRef,
  u,
  origin,
  fromRef,
  fromS,
  fromDatum,
  toRef,
  toS,
  toDatum,
  style,
}) {
  const fromPoint = pointAt(origin, u, fromS);
  const toPoint = pointAt(origin, u, toS);
  return {
    rule,
    subjectRef,
    axis: u,
    from: {
      ref: fromRef,
      point: fromPoint,
      // Where the extension line carries on to, when the wall does not reach
      // the line this dimension was taken on. Null when it already does.
      anchor: fromDatum
        ? datumExtensionAnchor(fromDatum, fromS, fromPoint, u)
        : null,
    },
    to: {
      ref: toRef,
      point: toPoint,
      anchor: toDatum ? datumExtensionAnchor(toDatum, toS, toPoint, u) : null,
    },
    distance: Math.abs(toS - fromS),
    style,
  };
}

/**
 * CENTERED-BETWEEN — the subject sits equidistant between two named
 * boundaries. Emits one leg to each, so the drawing states the relationship
 * rather than asserting it: if the subject has drifted, the two legs differ
 * and print their real numbers instead of EQ.
 */
function centeredBetween(record, ctx) {
  const u = resolveAxis(record, ctx, axisModeFor(record.rule));
  const origin = subjectCentre(record, ctx);
  if (!u || !origin) return [];

  const [refA, refB] = Array.isArray(record.datum) ? record.datum : [];
  // A half-stated record — one slot still empty — measures nothing. That is
  // an ordinary intermediate state while the second datum is being picked.
  if (!refA || !refB) return [];
  const a = resolveDatum(refA, ctx, u);
  const b = resolveDatum(refB, ctx, u);
  if (!a || !b) return [];

  const s0 = dotXZ({ x: origin[0], z: origin[1] }, u);
  // Measure to the FACING side of each datum — the edge the subject sees.
  // For a wall that is its near face, not the centreline inside it.
  const sA = a.isWall ? datumEdgeToward(a, s0) : a.center < s0 ? a.hi : a.lo;
  const sB = b.isWall ? datumEdgeToward(b, s0) : b.center < s0 ? b.hi : b.lo;

  const style =
    Math.abs(Math.abs(s0 - sA) - Math.abs(sB - s0)) <= EQ_TOLERANCE_M
      ? 'eq'
      : 'dimension';

  const base = {
    rule: 'centered-between',
    subjectRef: record.subject,
    u,
    origin,
    style,
  };
  return [
    leg({
      ...base,
      fromRef: refA,
      fromS: sA,
      fromDatum: a,
      toRef: record.subject,
      toS: s0,
    }),
    leg({
      ...base,
      fromRef: record.subject,
      fromS: s0,
      toRef: refB,
      toS: sB,
      toDatum: b,
    }),
  ];
}

/**
 * CENTERED-ON — the subject shares a centre with one datum. It is
 * `centered-between` the datum's own two edges, which is why they share a
 * resolver shape: centring on a window is centring between its jambs.
 */
function centeredOn(record, ctx) {
  const u = resolveAxis(record, ctx, axisModeFor(record.rule));
  const origin = subjectCentre(record, ctx);
  if (!u || !origin) return [];

  const ref = Array.isArray(record.datum) ? record.datum[0] : record.datum;
  const d = resolveDatum(ref, ctx, u);
  // A datum with no extent has no two edges to sit between; there is nothing
  // to draw, and a zero-length dimension would be worse than none.
  if (!d || d.crossing || !(d.hi - d.lo > EQ_TOLERANCE_M)) return [];

  const s0 = dotXZ({ x: origin[0], z: origin[1] }, u);
  const style =
    Math.abs(Math.abs(s0 - d.lo) - Math.abs(d.hi - s0)) <= EQ_TOLERANCE_M
      ? 'eq'
      : 'dimension';

  const base = {
    rule: 'centered-on',
    subjectRef: record.subject,
    u,
    origin,
    style,
  };
  // Both ends name the datum, so each leg can carry its dotted line back to
  // the object rather than stopping where the object is not.
  return [
    leg({
      ...base,
      fromRef: ref,
      fromS: d.lo,
      fromDatum: d,
      toRef: record.subject,
      toS: s0,
    }),
    leg({
      ...base,
      fromRef: record.subject,
      fromS: s0,
      toRef: ref,
      toS: d.hi,
      toDatum: d,
    }),
  ];
}

/**
 * OFFSET-FROM — one dimension, the datum's centreline to the subject's centre.
 * The stated `value` is what the geometry SHOULD be; the drawing reports what
 * it is, so an unenforced record that has drifted reads as drifted.
 */
function offsetFrom(record, ctx) {
  const u = resolveAxis(record, ctx, axisModeFor(record.rule));
  const origin = subjectCentre(record, ctx);
  if (!u || !origin) return [];

  const ref = Array.isArray(record.datum) ? record.datum[0] : record.datum;
  const d = resolveDatum(ref, ctx, u);
  if (!d) return [];

  const s0 = dotXZ({ x: origin[0], z: origin[1] }, u);
  // A wall is measured from its FACE; everything else from its centreline.
  const from = datumEdgeToward(d, s0);
  if (Math.abs(s0 - from) <= EQ_TOLERANCE_M) return [];

  return [
    leg({
      rule: 'offset-from',
      subjectRef: record.subject,
      u,
      origin,
      fromRef: ref,
      fromS: from,
      fromDatum: d,
      toRef: record.subject,
      toS: s0,
      style: 'dimension',
    }),
  ];
}

// The registry is CLOSED: a record naming a rule that is not here resolves to
// nothing rather than falling back, so a typo can never silently dimension to
// the wrong datum.
const DIMENSION_RULES = {
  'centered-between': { label: 'Centred between', resolve: centeredBetween },
  'centered-on': { label: 'Centred on', resolve: centeredOn },
  'offset-from': { label: 'Offset from', resolve: offsetFrom },
};

/** Resolve every record to the measurements it draws. PURE. */
function resolveDimensions(records, ctx) {
  if (!Array.isArray(records)) return [];
  const out = [];
  for (const record of records) {
    const rule = DIMENSION_RULES[record?.rule];
    if (!rule) continue;
    out.push(...rule.resolve(record, ctx));
  }
  return out;
}

module.exports = { DIMENSION_RULES, resolveDimensions, EQ_TOLERANCE_M };
